import React from "react";
import moment from "moment";
import { formatMelbourneDayLabel, formatMelbourneDateTime } from "../../../utils/melbourneTime";
import { Bed, Bath, Car, Clock, Pencil, StickyNote, ExternalLink, Thermometer, Database, CheckCircle2 } from "lucide-react";
import MarkLostMenu from "./MarkLostMenu";
import { withdrawnReasonLabel } from "../constants/lostReasons";

const formatPrice = (price) => {
    if (price == null || price === "") return null;
    const num = Number(price);
    if (Number.isNaN(num)) return price;
    return `$${Math.round(num).toLocaleString()}`;
};

/**
 * ONE-LINE version of PipelinePropertyCard, used by the pipeline list view.
 *
 * Simon Abbott, 3 Sep 2026: "These need to be much smaller. We should be able
 * to see about 10 appraisals on the one page. Once we have hundreds of
 * appraisals in the CRM it will be too hard to scroll. Show all the relevant
 * info but shrink them to a smaller list view."
 *
 * So every field the card carried is still here - address, price, beds/baths/
 * parking, primary agent, expected commission, method of sale, age and the
 * latest note - just laid out across a single row instead of down a panel.
 * Yield was dropped from the row: it reads N/A on every appraisal without a
 * weekly rent, which is nearly all of them, and it is on the property page.
 */
const PipelinePropertyRow = ({ property, theme, onClick, onEdit, onChangeTemperature, onAddToDatabase, onListed, onLost }) => {
    const address =
        property.address ||
        [property.street_name, property.suburb, property.state].filter(Boolean).join(", ") ||
        property.title ||
        "Property Address";
    const agentName = property.agent?.name || "—";

    const priceVal = property.price != null && property.price !== "" ? parseFloat(property.price) : null;
    const priceTo = property.price_to != null && property.price_to !== "" ? parseFloat(property.price_to) : null;
    const priceDisplay = property.price_display || "exact";

    const priceLabel = (() => {
        if (priceDisplay === "range" && priceVal != null && priceTo != null && Number(priceTo) !== Number(priceVal)) {
            return `${formatPrice(priceVal)} – ${formatPrice(priceTo)}`;
        }
        if (priceVal != null) return formatPrice(priceVal);
        return "No price yet";
    })();

    const price = parseFloat(property.price) || 0;
    const rate = property.commission_rate != null ? parseFloat(property.commission_rate) : null;
    const amount = property.commission_amount != null ? parseFloat(property.commission_amount) : null;
    const estimatedCommission =
        amount != null && amount > 0 ? amount : rate != null && rate > 0 && price > 0 ? (rate / 100) * price : 0;

    const timeAgo = property.created_at ? moment(property.created_at).fromNow() : "—";

    const method = (property.method_of_sale || "Private Sale").trim();
    const methodLabel =
        method.toLowerCase() === "auction" && property.auction_date
            ? `AUCTION ${formatMelbourneDayLabel(property.auction_date)}`
            : method;

    const parkingCount =
        (Number(property.parkings) || 0) +
            (Number(property.car_ports) || 0) +
            (Number(property.open_spaces) || 0) +
            (Number(property.parking_spaces) || 0) || Number(property.parking) || 0;

    const weeklyRent = property.weekly_rent != null && property.weekly_rent !== "" ? property.weekly_rent : null;
    const latestNote = property.latest_note;
    const isScheduled = property.listing_launch_at && new Date(property.listing_launch_at) > new Date();

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onClick?.(property)}
            onKeyDown={(e) => e.key === "Enter" && onClick?.(property)}
            className="bg-white rounded-lg border border-[#EEF0F2] hover:border-[#55C5D0]/50 hover:shadow-sm transition-all cursor-pointer font-sans text-[#353B48] px-3 py-2"
        >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                {/* Address + price */}
                <div className="min-w-0 sm:flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-[13px] font-bold text-[#333333] truncate">{address}</h3>
                        {property.is_withdrawn && (
                            <span className="shrink-0 text-[9px] font-bold text-[#DC2626] bg-[#FDECEC] border border-[#E5484D]/30 rounded px-1.5 py-0.5 uppercase tracking-wide">
                                Lost{property.withdrawn_reason ? ` · ${withdrawnReasonLabel(property.withdrawn_reason)}` : ""}
                            </span>
                        )}
                        {isScheduled && (
                            <span className="shrink-0 text-[9px] font-bold text-[#55C5D0] bg-[#55C5D0]/10 border border-[#55C5D0]/20 rounded px-1.5 py-0.5">
                                SCHEDULED
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-[#5F6368] font-medium">
                        <span className={priceVal == null ? "text-[#9CA3AF] italic" : ""}>{priceLabel}</span>
                        {weeklyRent && <span className="text-[#9CA3AF]">· {formatPrice(weeklyRent)}/wk</span>}
                        <span className="hidden sm:inline text-[10px] text-[#9CA3AF] uppercase tracking-wide">
                            · {methodLabel}
                        </span>
                    </div>
                </div>

                {/* Beds / baths / parking */}
                <div className="flex items-center gap-3 shrink-0 text-[#5F6368]">
                    <span className="flex items-center gap-1">
                        <Bed size={14} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[11px] font-medium">{property.beds ?? 0}</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <Bath size={14} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[11px] font-medium">{property.baths ?? 0}</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <Car size={14} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[11px] font-medium">{parkingCount}</span>
                    </span>
                </div>

                {/* Primary agent */}
                <div className="flex items-center gap-1.5 min-w-0 sm:w-[140px] shrink-0">
                    <div className="w-5 h-5 rounded-full bg-[#55C5D0] flex items-center justify-center text-white text-[9px] font-bold shrink-0">
                        {agentName.charAt(0).toUpperCase() || "—"}
                    </div>
                    <span className="text-[11px] font-medium truncate">{agentName}</span>
                </div>

                {/* Expected commission */}
                <div className={`${theme.commBg} rounded-md px-2 py-1 shrink-0 sm:w-[112px] text-center`}>
                    <span className={`font-bold text-[11px] ${theme.text} whitespace-nowrap`}>
                        ${estimatedCommission > 0 ? Math.round(estimatedCommission).toLocaleString() : "—"}
                    </span>
                </div>

                {/* Age */}
                <div className="flex items-center gap-1 text-[#9CA3AF] text-[10px] font-medium shrink-0 sm:w-[86px]">
                    <Clock className="w-3 h-3 shrink-0" />
                    <span className="truncate">{timeAgo}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0 ml-auto sm:ml-0">
                    {/* The list view had no way to change temperature at all —
                        only dragging a card on the board did, which the list has
                        no cards for (Simon Abbott, Monday 12985995388). */}
                    {onChangeTemperature && (
                        <button
                            type="button"
                            title="Move to Cold / Warm / Hot"
                            aria-label="Move this appraisal to Cold, Warm or Hot"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeTemperature(property);
                            }}
                            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F89542] hover:bg-[#F89542]/10 transition-colors"
                        >
                            <Thermometer className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {/* Same Add to Database action the board cards carry, so
                        an agent working the list never has to go back to the
                        board for it (Paul Jimoulis, CRM-2026-00070). */}
                    {onAddToDatabase && (
                        <button
                            type="button"
                            title="Add this appraisal to your Database"
                            aria-label="Add this appraisal to your Database"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddToDatabase(property);
                            }}
                            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                        >
                            <Database className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {onEdit && (
                        <button
                            type="button"
                            title="Edit appraisal details"
                            aria-label="Edit appraisal details"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(property);
                            }}
                            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#55C5D0] hover:bg-[#55C5D0]/10 transition-colors"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {onListed && (
                        <button
                            type="button"
                            title="Mark as Listed (moves to My Listings as a draft)"
                            aria-label="Mark as Listed"
                            onClick={(e) => {
                                e.stopPropagation();
                                onListed(property);
                            }}
                            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#2b9aa4] hover:bg-[#55C5D0]/10 transition-colors"
                        >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {onLost && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <MarkLostMenu
                                onSelect={(reason, note) => onLost(property, reason, note)}
                                label=""
                                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#c8392f] hover:bg-[#c8392f]/10 transition-colors"
                            />
                        </div>
                    )}
                    <button
                        type="button"
                        title="View / Edit"
                        aria-label="View or edit this appraisal"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick?.(property);
                        }}
                        className="p-1.5 rounded-lg text-[#55C5D0] hover:bg-[#55C5D0]/10 transition-colors"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* Latest note - one line, so it never pushes the row out of shape. */}
            {latestNote?.content && (
                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-600 min-w-0">
                    <StickyNote className="w-3 h-3 text-amber-500 shrink-0" />
                    <span className="truncate">{latestNote.content}</span>
                    <span className="text-[10px] text-gray-400 shrink-0 hidden sm:inline">
                        {latestNote.author_name || "Unknown"}
                        {latestNote.created_at ? ` · ${formatMelbourneDateTime(latestNote.created_at)}` : ""}
                    </span>
                </div>
            )}
        </div>
    );
};

export default PipelinePropertyRow;
